import { Group, Progress, Stack, Text, ThemeIcon, Tooltip } from '@mantine/core';
import { Sparkline } from '@mantine/charts';
import { IconArrowDown, IconArrowUp, IconInfoCircle, IconMinus } from '@tabler/icons-react';

import { CardShellView } from '../atoms/CardShell';
import type { MetricCardStyling, MetricCardViewProps } from './MetricCard.types';

const STYLING_COLOR: Record<MetricCardStyling, string> = {
  positive: 'teal',
  neutral: 'blue',
  negative: 'red',
  attention: 'yellow',
};

const VALUE_SIZE = { sm: 20, md: 28, lg: 38 } as const;
const LABEL_SIZE = { sm: 'xs', md: 'sm', lg: 'md' } as const;
const PADDING = { sm: 'xs', md: 'sm', lg: 'md' } as const;

export function MetricCardView({
  label,
  tooltip,
  value,
  caption,
  icon,
  headerAction,
  styling = 'neutral',
  delta,
  progress,
  footer,
  size = 'md',
  variant = 'amount',
  loading = false,
  withBorder = true,
  onClick,
  active = false,
}: MetricCardViewProps) {
  const color = STYLING_COLOR[styling];
  const valueFz = VALUE_SIZE[size];
  const labelSize = LABEL_SIZE[size];

  const deltaIcon =
    delta?.direction === 'up' ? (
      <IconArrowUp size={12} />
    ) : delta?.direction === 'down' ? (
      <IconArrowDown size={12} />
    ) : (
      <IconMinus size={12} />
    );

  const deltaColor =
    delta?.direction === 'up'
      ? STYLING_COLOR.positive
      : delta?.direction === 'down'
        ? STYLING_COLOR.negative
        : 'gray';

  const hasSparkline = variant === 'delta' && delta?.sparklineData && delta.sparklineData.length > 0;

  return (
    <CardShellView
      size={size}
      loading={loading}
      withBorder={withBorder}
      onClick={onClick}
      padding={PADDING[size]}
      skeletonRows={[{ height: 12, width: '50%' }, { height: valueFz + 8, width: '40%' }, { height: 10, width: '70%' }]}
      style={({ hovered }) => ({
        cursor: onClick ? 'pointer' : undefined,
        transition: 'background-color 120ms, border-color 120ms, box-shadow 120ms',
        backgroundColor: active ? `var(--mantine-color-${color}-0)` : undefined,
        borderColor: active
          ? `var(--mantine-color-${color}-5)`
          : onClick && hovered
            ? `var(--mantine-color-${color}-3)`
            : undefined,
        boxShadow: onClick && hovered && !active ? 'var(--mantine-shadow-sm)' : undefined,
      })}
    >
      <Stack gap={4}>
        <Group justify="space-between" align="flex-start" wrap="nowrap" gap="xs">
          <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
            {tooltip ? (
              <Tooltip label={tooltip} withArrow multiline maw={260}>
                <Group gap={4} wrap="nowrap" style={{ cursor: 'default', width: 'fit-content' }}>
                  <Text size={labelSize} c="dimmed" fw={500} truncate>
                    {label}
                  </Text>
                  <IconInfoCircle size={12} style={{ color: 'var(--mantine-color-dimmed)', flexShrink: 0 }} />
                </Group>
              </Tooltip>
            ) : (
              <Text size={labelSize} c="dimmed" fw={500} truncate>
                {label}
              </Text>
            )}

            <Group align="baseline" gap={6} wrap="nowrap">
              <Text fw={700} fz={valueFz} lh={1}>
                {value}
              </Text>

              {variant === 'delta' && delta && (
                <Group gap={2} wrap="nowrap" c={deltaColor}>
                  {deltaIcon}
                  <Text size="xs" fw={500}>
                    {delta.value}
                  </Text>
                </Group>
              )}
            </Group>

            {variant === 'progress' && progress !== undefined && (
              <Stack gap={4} mt={4}>
                <Progress value={progress.value} color={color} size="sm" radius="xl" />
                {progress.label && (
                  <Text size="xs" c="dimmed">
                    {progress.label}
                  </Text>
                )}
              </Stack>
            )}

            {caption && (
              <Text size="xs" c="dimmed" mt={4}>
                {caption}
              </Text>
            )}
          </Stack>

          <Stack align="flex-end" gap={4} style={{ flexShrink: 0 }}>
            {icon && (
              <ThemeIcon variant="light" color={color} size={size === 'sm' ? 'sm' : 'md'} radius="md">
                {icon}
              </ThemeIcon>
            )}
            {headerAction}
          </Stack>
        </Group>

        {hasSparkline && (
          <Sparkline
            w="100%"
            h={size === 'lg' ? 40 : 28}
            data={delta!.sparklineData!}
            color={color}
            strokeWidth={1.5}
          />
        )}

        {footer}
      </Stack>
    </CardShellView>
  );
}
